/* The session cache the two whole-collection CRM reads share.

   useAllInteractions.js pulls every `interactions` document for the ⌘K
   palette; useOpenItems.js pulls every unresolved open item for the Clients
   list's Open column. Both want the same three things, and both had their own
   half of them:

   - one read per session, shared by every component that asks;
   - a backoff, so a collection that reliably denies (an undeployed rule) is
     not re-read on every debounced keystroke;
   - a way to drop everything when the session ends, because these are two of
     the most sensitive collections in the app and a cached copy must not
     outlive the sign-out that ended the right to it.

   It lives here rather than inside either hook because a hook cannot be
   rendered in this repo's tests (no DOM environment), and this is the part
   worth testing. The fetcher is injected, so the tests never touch Firestore. */

/** How long a failed load blocks the next attempt. */
export const RETRY_AFTER_FAILURE_MS = 30_000;

/**
 * A failure latch.
 *
 * Clearing the cache outright after a failure is the wrong half of the fix:
 * load() is called from a debounced keystroke effect, so a collection that
 * reliably denies would re-issue a failing read on every word typed. So a
 * failure clears the cache AND starts a backoff, and nothing retries until it
 * expires.
 */
export function makeRetryGate(backoffMs = RETRY_AFTER_FAILURE_MS) {
  let failedAt = 0;
  return {
    /** May a load be attempted right now? */
    allows(now = Date.now()) {
      return !failedAt || now - failedAt >= backoffMs;
    },
    /** Record a failure; blocks further attempts for the backoff window. */
    fail(now = Date.now()) {
      failedAt = now;
    },
    /** Forget any failure — a successful load, or a test resetting state. */
    reset() {
      failedAt = 0;
    },
  };
}

/**
 * One collection, read once per session.
 *
 * `load()` returns the shared promise of `{ records, error }` — `error` is the
 * failure that produced an empty list, or null. It returns null when the
 * backoff is holding the door shut, which is not the same as an empty result
 * and callers must not treat it as one.
 *
 * `inFlight` is what tells a component that attached to someone else's read
 * that it is still waiting. Without it a second component mounting mid-read
 * sat at loading:false with no records — an empty list that looks settled.
 *
 * @param fetcher  () => Promise<Array>, the actual read
 * @param label    prefix for the one console.warn a failure prints
 */
export function makeSessionCache(fetcher, { gate = makeRetryGate(), label = "[sessionCache]" } = {}) {
  let cache = null;
  let inFlight = false;

  return {
    /** Is a read running right now, whoever started it? */
    get inFlight() { return inFlight; },
    /** Is anything cached (settled or in flight)? */
    has() { return cache !== null; },

    /** Drop everything. Called on sign-out and on losing the role. */
    clear() {
      cache = null;
      inFlight = false;
      gate.reset();
    },

    /** Test seam: stand in for a read that already succeeded. */
    seed(records) {
      cache = Promise.resolve({ records: records || [], error: null });
      inFlight = false;
      gate.reset();
    },

    load() {
      if (cache) return cache;
      if (!gate.allows()) return null;
      inFlight = true;
      /* Both completion handlers touch shared state ONLY while this read is
         still the cached one. clear() (sign-out, role loss) makes it stale,
         and a stale read that settles afterwards must not close the gate on,
         or mark as settled, the next session's read. (Closing Codex review,
         2026-09-06: a stale rejection re-armed the backoff so a freshly
         signed-in user's open-items query stayed unloaded until remount.) */
      const pending = fetcher().then(
        (records) => {
          if (cache === pending) {
            inFlight = false;
            gate.reset();
          }
          return { records, error: null };
        },
        (err) => {
          if (cache === pending) {
            inFlight = false;
            // A failed load must not poison the session cache — one permission
            // hiccup would otherwise last until a reload.
            cache = null;
            gate.fail();
          }
          // console.warn, not error: an undeployed rule is an expected state
          // and must not read as a crash.
          console.warn(label, err?.code || err?.message || err);
          return { records: [], error: err };
        },
      );
      cache = pending;
      return pending;
    },
  };
}
